import { useEffect, useState } from 'preact/hooks';
import { startRegistration, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { CaptureGlyph as Knot } from '../components/CaptureGlyph';
import { AgeQuestion, AGE_ANSWER_SAVE_FAILED_MESSAGE } from '../components/AgeQuestion';
import { setStoredTenantId, setStoredTenantVertical } from '../lib/tenant';
import { useDeploymentSkin } from '../lib/skin';
import { claimSelf, ClaimSelfError } from '../lib/claimSelf';
import { MaterialsPhotos } from './MaterialsPhotos';
import { YourPage } from './YourPage';
import { CaptureSheet } from '../components/CaptureSheet';
import {
  createTenant,
  getDeploymentVertical,
  updateActorProfile,
  passkeyRegisterOptions,
  passkeyRegisterVerify,
  ApiError,
  type ApiUser,
  getMailbox,
  setMailboxSlug,
  getMailInDomain,
  updateMyName,
  markSetupFinished,
  recordAgeAttestation,
  listHouseholdPerformers,
  getPlan,
  planHas,
  type PlanApi,
} from '../lib/api';
import './Onboarding.css';

export interface OnboardingProps {
  /** The just-signed-in user (a brand-new account with zero workspaces). */
  user: ApiUser;
  /** The calm, non-blocking "we emailed you a link to confirm your address"
   *  notice carried over from a fresh password registration. */
  notice?: string;
  /** Handed the newly-created tenantId once setup completes; App stores it as
   *  active and drops into first-run Today. */
  onResolved: (tenantId: string) => void;
  /** ⚑ PICK THE WALK BACK UP (2026-09-16 — the interrupted-onboarding defect).
   *
   * When set, this household ALREADY EXISTS and its setup never reached the
   * end: the person reloaded, or closed the tab, somewhere after the workspace
   * was created. The name and union are already written, so the screen skips
   * straight to the questions that were still owed — the household's address,
   * then the passkey offer — and nothing is created a second time.
   *
   * App passes it when the membership says `setupInProgress`; see the gate
   * there, and POST /auth/tenants/setup-finished for what turns it off. */
  resumeTenantId?: string;
  /** ⚑ THE NAME THEY TYPED ON THE FIRST SCREEN, ONCE IT IS SAVED (2026-09-15).
   *
   * Same prop Settings already carries — App holds the signed-in user in
   * state, so a write to `users.name` that nobody hands back leaves every
   * screen greeting them by the OLD value until the next reload. Optional, so
   * a caller that does not care keeps its existing call byte-for-byte. */
  onUserUpdated?: (user: ApiUser) => void;
}

// The actor's setup vertical — set FOR the user (the word "vertical" is never
// shown; per Huan 2026-07-13 first-run collects NAME + UNION only).
const VERTICAL = 'actors';

const UNIONS = ['ACTRA', 'SAG-AFTRA', 'Both', 'Non-union'] as const;
type Union = (typeof UNIONS)[number];

// Map a chip to what actually gets stored as union_name. "Both" is the two
// unions; "Non-union" is recorded verbatim (free-text field) rather than left
// blank, so a later screen can tell "chose non-union" from "skipped".
const UNION_NAME: Record<Union, string> = {
  ACTRA: 'ACTRA',
  'SAG-AFTRA': 'SAG-AFTRA',
  Both: 'ACTRA / SAG-AFTRA',
  'Non-union': 'Non-union',
};

// B1 (2026-09-24) — 'age' is the setup-age step ("Is {performerName} 18 or
// over?"), actor_assist only, right after 'name'. In the setup, not the
// beta flow, per Huan's own reasoning: "when we remove the beta process...
// we don't lose the question."
//
// B5 (2026-09-24, decision `3b499818`) — 'headshots' and 'resume_import' are
// the first two of Huan's three first-run materials stops, actor_assist
// only, right after the passkey offer (or its skip). The third stop his
// ruling names — "anything else you send out" (reels/clips) — has no live
// upload surface in this deployment's actual actor_assist Materials tab to
// reuse (see `actorAssistSteps` below and this chunk's report); rather than
// invent one, it is not built. 'resume_import' is itself plan-gated
// (`resume.import_old` — Free-and-up since B7, 2026-09-24, `defa9aed`) and is
// skipped outright, never shown locked, only for a tenant whose plan read is
// in flight or failed — see `actorAssistSteps`.
type Step =
  | 'name'
  | 'age'
  | 'union'
  | 'creating'
  | 'resuming'
  | 'address'
  | 'passkey'
  | 'headshots'
  | 'resume_import'
  // B8 — the seam's own step: preview/address/publish/live, for an adult-
  // attested performer only (see `continueToPage`'s own header).
  | 'page';

function defaultName(user: ApiUser): string {
  const trimmed = user.name?.trim();
  if (trimmed) return trimmed;
  const hint = user.performerNameHint?.trim();
  if (hint) return hint;
  const [local] = user.email.split('@');
  return local ?? '';
}

// Auth-2 first-run (new actor, zero tenants) — matches the blessed
// onboarding_flow.html column B: name, then union chips, progress dots, the
// breathing Knot on the "all set" beat, then first-run Today. Everything else
// (home base, reps, materials) is deferred. An OPTIONAL Face/Touch ID prompt
// follows setup where the browser supports it.
export function Onboarding({ user, notice, onResolved, onUserUpdated, resumeTenantId }: OnboardingProps) {
  const [step, setStep] = useState<Step>(resumeTenantId ? 'resuming' : 'name');
  const [name, setName] = useState(defaultName(user));
  // B2 — actor_assist's own second name field, name step only. "Legal name
  // upfront. Cleaner that way as they are in setup mode." (Huan). Unused by
  // every other pack.
  const [legalName, setLegalName] = useState('');
  const [union, setUnion] = useState<Union | null>('ACTRA');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** B2 — the performer-record step's own failure, shown ON the 'creating'
   *  step rather than reverting to 'name'/'age': the workspace already
   *  exists by the time this can fail, and going back would risk a second
   *  `createTenant` on the next Continue. Null clears it; set, it swaps the
   *  spinner for the server's own sentence and a retry button. */
  const [claimError, setClaimError] = useState<string | null>(null);
  // Set once the tenant + profile are written, so a passkey-step failure/skip
  // never re-creates the workspace.
  const [tenantId, setTenantId] = useState<string | null>(null);
  /** B8 — the setup-age step's own answer, held for `continueToPage` below.
   *  Null until the age step actually runs (every other pack, and the
   *  resumed-walk path that skips straight past it). */
  const [selfIsAdult, setSelfIsAdult] = useState<boolean | null>(null);
  /** B5 — the résumé-import materials stop's own capture sheet, mounted
   *  directly on this screen (see the 'resume_import' step render). */
  const [resumeSheetOpen, setResumeSheetOpen] = useState(false);
  /** The household's inbound address, and the handle inside it.
   *
   * ⚑ THIS STEP DISAPPEARS COMPLETELY when there is no address — inbound mail
   * unwired on this deployment, or no usable slug. Today that is every
   * deployment, so the flow is byte-for-byte what it was; the step appears
   * only once there is something true to show. Never a placeholder: a parent
   * shown an address will hand it to a casting director. */
  const [mailAddress, setMailAddress] = useState<string | null>(null);
  const [handle, setHandle] = useState('');
  const [handleError, setHandleError] = useState<string | null>(null);
  // THE DEPLOYMENT'S OWN VERTICAL (found live 2026-08-10, first real walk of
  // the Rexx Box build): this screen used to hardcode 'actors' — it created
  // the first real Rexx workspace mislabeled, then 422'd on the Actor-only
  // profile write and walled the person after the union step. The server now
  // stamps the vertical it runs (POST /auth/tenants ignores a client guess on
  // a packs build) and serves it at GET /auth/entry; this screen asks once
  // and SKIPS pack-foreign steps. null (pre-packs builds: Actor, older
  // Personal) keeps the legacy Actor flow byte-for-byte.
  const [deploymentVertical, setDeploymentVertical] = useState<string | null>(null);
  /** null until asked, and null FOREVER on a deployment with no inbound mail —
   *  which is every deployment today. Gates the address step's existence. */
  const [mailInDomain, setMailInDomain] = useState<string | null>(null);
  useEffect(() => {
    getDeploymentVertical().then(setDeploymentVertical).catch(() => {});
    getMailInDomain().then(setMailInDomain).catch(() => {});
  }, []);
  /** B5 — the household's own tier, fetched once the workspace exists, so the
   *  résumé-import materials stop can gate itself on `resume.import_old`
   *  (Free-and-up since B7, `defa9aed`) rather than showing a plan-withheld
   *  read a door that would 403. null reads as "not included yet" (same
   *  safe-default `planHas` itself takes) while the read is in flight or on
   *  a deployment too old to answer — a plan-withheld stop is SKIPPED,
   *  never shown locked, in first-run. */
  const [plan, setPlan] = useState<PlanApi | null>(null);
  useEffect(() => {
    if (!tenantId || deploymentVertical !== 'actor_assist') return;
    let cancelled = false;
    getPlan(tenantId)
      .then((p) => {
        if (!cancelled) setPlan(p);
      })
      .catch(() => {
        // Not an answer either way — the stop stays gated off, the safe
        // direction `planHas` already takes on a null plan.
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, deploymentVertical]);
  // B2 — this screen and BetaInvite were the two doors that stayed blue on
  // a skinned deployment; Login.tsx's own pattern, shared via lib/skin.ts so
  // the three can never drift. Its own `getDeploymentVertical()` read is
  // independent of the one above (that one drives step branching and this
  // one only ever feeds `resolvePack`) — same posture Login already took.
  useDeploymentSkin();
  // ⚑ 2026-09-19 — FOUND ON A REAL WALK, THEN PARTIALLY REVERTED. This used
  // to test only 'actors' (the frozen legacy pack), so a fresh actor_assist
  // signup silently took the GENERIC branch below. Widening this to also
  // treat 'actor_assist' as the actor flow looked right but is NOT safe on
  // its own: this flow's `updateActorProfile` call (a few lines down) is
  // `PUT /api/actor/profile`, a route built for the OLD `actor` pack's own
  // profile record — it 404s for an actor_assist tenant, which has no such
  // route and claims its performer a different way (aa_subject, via
  // ClaimYourself on Today). Wiring that up is a real gap this chunk did
  // not create and should not paper over by guessing at a fix here. Left as
  // 'actors'-only; the copy this generic branch shows for actor_assist is
  // fixed just below instead, and the gap is reported rather than patched.
  const isActorFlow = (deploymentVertical ?? 'actors') === 'actors';

  /** B2 §3 — the steps an actor_assist signup will actually see, in order:
   *  name -> age -> address (only when this deployment has a mail-in
   *  domain) -> passkey (only when the browser can do WebAuthn). Read off
   *  the same two signals the flow itself already branches on
   *  (`mailInDomain`, `browserSupportsWebAuthn()`), so the dots can never
   *  promise a step that never comes. Other packs keep their own fixed
   *  dot count, unchanged. */
  function actorAssistSteps(): Step[] {
    const steps: Step[] = ['name', 'age'];
    if (mailInDomain) steps.push('address');
    if (browserSupportsWebAuthn()) steps.push('passkey');
    // B5 — first-run materials, after the passkey offer. Headshots always
    // shows (uploading a photo carries no feature key); the résumé import
    // shows only where the plan actually grants it.
    steps.push('headshots');
    if (planHas(plan, 'resume.import_old')) steps.push('resume_import');
    return steps;
  }

  function actorAssistDots(current: Step) {
    const steps = actorAssistSteps();
    const idx = steps.indexOf(current);
    return (
      <div class="onb__dots">
        {steps.map((s, i) => (
          <i key={s} class={`onb__dot${i === idx ? ' onb__dot--on' : ''}`} />
        ))}
      </div>
    );
  }

  /** ⚑ SETTING UP IS OVER — SAID OUT LOUD, THEN HANDED ON (2026-09-16).
   *
   * Every exit from this screen runs through here. Until now the screen just
   * called `onResolved` and left no trace that it had ever finished, so a
   * reload part-way through was indistinguishable from a completed setup — and
   * the app, having nothing to read, assumed the generous thing and assumed
   * wrong. The mark is what makes "interrupted" a state the product can see.
   *
   * ⚠ IT NEVER BLOCKS. A failed mark means the address question is asked once
   * more next time, which costs a family one tap. The opposite failure — going
   * on without it — costs them an address they never chose and can no longer
   * change. */
  function finishSetup(newTenantId: string): void {
    setStoredTenantId(newTenantId);
    // ⚠ NOT AWAITED, AND THAT IS THE C6a INVARIANT (see the passkey step's
    // "Skip for now" below). Moving on must never wait on a request: awaiting
    // this put a network round trip in front of the skip control, which is
    // precisely the shape of the 2026-08-04 freeze that made skippability an
    // invariant in the first place. The mark is a note to the next boot, not a
    // condition of finishing — so it is sent, and the family goes on.
    void markSetupFinished(newTenantId).catch(() => {
      // Nothing to say to anyone: the household exists, every question was
      // asked, and the only cost is being asked one of them again.
    });
    onResolved(newTenantId);
  }

  /** ⚑ B8 — THE PAGE STEP ITSELF (decision `defa9aed`), filling the seam
   *  B5 §4 left ("every path through the materials stops ends HERE… so the
   *  next chunk can replace this one body without touching where it is
   *  called from").
   *
   *  ⚑ MINORS SKIP IT IN FIRST-RUN (the brief's own words): an account whose
   *  age attestation is "No, under 18" (or has none — `selfIsAdult` is
   *  `null` whenever the age step never ran, e.g. a resumed walk) goes
   *  straight to Today. This is answered LOCALLY, off the tap `answerAge`
   *  already recorded — no round trip, and no flash of the page step before
   *  bouncing off it. The guardian flow F5 built for a minor's own page is a
   *  later chunk; YourPage.tsx's own header has the full reasoning for why
   *  this is the one question first-run needs answered here. */
  function continueToPage(id: string): void {
    if (deploymentVertical === 'actor_assist' && selfIsAdult === true) {
      setBusy(false);
      setStep('page');
      return;
    }
    finishSetup(id);
  }

  /** ⚑ B5 §1 — THE TAIL AFTER THE PASSKEY OFFER (or its skip). Every one of
   *  the four places setup used to call `finishSetup` straight from here
   *  now funnels through this one function instead, so the materials stops
   *  cannot drift out of sync with each other. actor_assist walks its two
   *  live materials stops (headshots always; the résumé import only where
   *  the plan grants it — see `actorAssistSteps`) before `continueToPage`;
   *  every other pack finishes exactly as it always has. `deploymentVertical`
   *  unresolved reads as "not actor_assist" — the safe direction, same
   *  posture `isActorFlow` already takes a few lines up. */
  function goToMaterialsOrFinish(id: string): void {
    if (deploymentVertical === 'actor_assist') {
      setBusy(false);
      setStep('headshots');
      return;
    }
    finishSetup(id);
  }

  /** The materials stops' own shared tail: headshots always leads here;
   *  résumé import shows only when the plan grants it. */
  function afterHeadshots(id: string): void {
    if (planHas(plan, 'resume.import_old')) {
      setStep('resume_import');
      return;
    }
    continueToPage(id);
  }

  /** The resumed walk: the workspace is already there, so this asks only what
   *  was still owed. A household with no address to name (no inbound mail on
   *  this deployment) has nothing left here and goes straight on. */
  useEffect(() => {
    if (!resumeTenantId) return;
    let cancelled = false;
    setTenantId(resumeTenantId);
    void (async () => {
      let address: string | null = null;
      try {
        const domain = await getMailInDomain();
        if (domain) address = await getMailbox(resumeTenantId);
      } catch {
        // A failed read is not a reason to hold a family in setup.
      }
      if (cancelled) return;
      if (address) {
        setMailAddress(address);
        setHandle(address.split('@')[0] ?? '');
        setStep('address');
        return;
      }
      continueAfterAddress(resumeTenantId);
    })();
    return () => {
      cancelled = true;
    };
    // Runs once for the household it was handed; nothing else re-opens it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeTenantId]);

  /** The tail every path through setup shares once the workspace itself
   *  exists: write the name they just typed, offer the address step if this
   *  deployment has one, then the passkey offer or straight to finished.
   *  Factored out of `createWorkspace` so B2's claim-self retry can reach it
   *  too, without re-running `createTenant`. */
  async function finishProfileAndAdvance(
    newTenantId: string,
    trimmedName: string,
    performerIsAdult: boolean | null
  ): Promise<void> {
    // ⚑ THE NAME THEY JUST TYPED IS WHO THE APP IS TALKING TO (2026-09-15,
    // the Day 1 walk).
    //
    // This screen asks for a name on its FIRST field and, until now, wrote
    // it nowhere a greeting reads. `users.name` is what Today's eyebrow and
    // every capture render (App's `displayName`, falling back to the email
    // local part), and the only thing that had ever written it was Settings
    // → Preferences → Your name. So a family that set up in the ordinary
    // way was greeted as "huan+parent" by a product that had just asked
    // them their name and been told.
    //
    // ⚠ WRITTEN HERE, NOT READ DIFFERENTLY THERE. The read was correct —
    // `users.name` IS the answer to "what do we call this person" — so the
    // fix is the missing write, not a second source for the greeting.
    //
    // ⚠ AND IT NEVER BLOCKS SETUP. A failed name write is a cosmetic loss
    // with a known repair one screen away; walling a family inside
    // onboarding over it would be the worse defect, and the same reasoning
    // the address step already applies to its own refusals.
    //
    // ⚑ BUT ON actor_assist THE NAME STEP ASKS FOR THE PERFORMER, and the
    // person signed in is only the performer when the age step said 18+
    // (2026-09-29, live defect: a parent setting up Kwame was greeted
    // "What's up, Kwame Osei?" — setup had renamed the PARENT's own user to
    // the child's professional name). Under 18, or no answer at all, the
    // signed-in person is someone else, so their own name is left exactly as
    // it was. The performer's name lives on the performer record `claimSelf`
    // just created, which is where every performer surface reads it.
    const namesTheSignedInPerson = deploymentVertical !== 'actor_assist' || performerIsAdult === true;
    if (namesTheSignedInPerson) {
      try {
        const updated = await updateMyName(trimmedName);
        if (updated) onUserUpdated?.(updated);
      } catch {
        // Nothing to say: the workspace exists, the name is editable in
        // Settings, and this is not a reason to stop setting up.
      }
    }
    // Offer Face/Touch ID only where the browser can actually do it.
    // ⚑ ONLY IF THIS DEPLOYMENT HAS INBOUND MAIL AT ALL. The public
    // `/auth/entry` answer is fetched on mount; a deployment with no
    // mail-in domain cannot give ANY household an address, so there is
    // nothing to ask `/api/mailbox` about and no call is made.
    try {
      const address = mailInDomain ? await getMailbox(newTenantId) : null;
      if (address) {
        setMailAddress(address);
        setHandle(address.split('@')[0] ?? '');
        setBusy(false);
        setStep('address');
        return;
      }
    } catch {
      // A failed read is not a reason to stop setting up.
    }

    if (browserSupportsWebAuthn()) {
      // Clear `busy` BEFORE handing over to the passkey step. When this
      // was found (2026-08-04, the first time a real person walked this
      // flow) BOTH of that step's controls were disabled={busy}, so the
      // stuck flag left a permanently frozen "Setting up…" button AND a
      // dead "Skip for now" — no way forward at all. C6a then made the
      // instance an INVARIANT: the skip control no longer carries any
      // disabled predicate, so even a stuck flag cannot close the exit —
      // but the flag is still cleared here so the primary renders idle.
      setBusy(false);
      setStep('passkey');
    } else {
      goToMaterialsOrFinish(newTenantId);
    }
  }

  /** B2 — the actor_assist name step creates the performer (Huan: "Legal
   *  name upfront. Cleaner that way as they are in setup mode."), reusing
   *  ClaimYourself's own three calls via `lib/claimSelf.ts` so the two can
   *  never drift. Returns whether setup may continue: true on success OR
   *  when a performer already exists (see below); false leaves `claimError`
   *  set and the caller must stay put.
   *
   *  ⚑ NO DUPLICATE PERFORMER. Migration 0142 allows exactly one active
   *  `aa_subject` per account and a second create is a raw 500. The cheapest
   *  existing read that answers "does this account already have its
   *  subject" is `listHouseholdPerformers` — the same one Today's own gate
   *  (`HouseholdHome`'s `unstarted`) already reads to decide whether to show
   *  ClaimYourself, so this asks the identical question the rest of the
   *  product already trusts. A failed READ here is not treated as "it
   *  exists" or "it doesn't" — it falls through to the create, which is the
   *  server's own word on the matter either way. */
  async function attemptClaim(newTenantId: string, professional: string, legal: string): Promise<boolean> {
    try {
      const existing = await listHouseholdPerformers(newTenantId);
      if (existing.length > 0) return true;
    } catch {
      // Not an answer either way — fall through to the create below.
    }
    try {
      await claimSelf(newTenantId, { legalName: legal, professionalName: professional });
      return true;
    } catch (err) {
      // Loud, never swallowed: the server's own sentence, on the setup
      // screen, with the step still put and the button live for a retry
      // (see the 'creating' step's render and `retryClaim` below).
      setClaimError(err instanceof Error ? err.message : String(err));
      return false;
    }
  }

  /** The 'creating' step's own retry, once a workspace exists but its
   *  performer record does not. Never calls `createTenant` again — retrying
   *  the whole act would mint a second workspace under the same account. */
  async function retryClaim(): Promise<void> {
    if (!tenantId || busy) return;
    setBusy(true);
    setClaimError(null);
    const ok = await attemptClaim(tenantId, name.trim(), legalName.trim());
    if (!ok) {
      setBusy(false);
      return;
    }
    await finishProfileAndAdvance(tenantId, name.trim(), selfIsAdult);
  }

  // Creates the workspace + writes name/union. `withUnion` false = the union
  // step was skipped, so no union_name is stored.
  //
  // `performerIsAdult` is passed IN by `answerAge` rather than read from
  // `selfIsAdult`: that setter has not re-rendered yet when `answerAge`
  // calls straight through to here, so the state would still read `null`.
  async function createWorkspace(withUnion: boolean, performerIsAdult: boolean | null = selfIsAdult) {
    // Single-flight guard IN the handler, not disabled= ON the skip control:
    // the C6a invariant is that no skip/later affordance ever shares a
    // disabled predicate with a submit control (a stuck busy flag froze BOTH
    // on 2026-08-04 — see the passkey step below).
    if (busy) return;
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Your name is needed to set up.');
      setStep('name');
      return;
    }
    if (deploymentVertical === 'actor_assist' && !legalName.trim()) {
      setError('Your legal name is needed to set up.');
      setStep('name');
      return;
    }
    setStep('creating');
    setError(null);
    setClaimError(null);
    setBusy(true);
    try {
      const requestedVertical = deploymentVertical ?? VERTICAL;
      const { tenantId: newTenantId, vertical: stampedVertical } = await createTenant(
        trimmed,
        requestedVertical
      );
      setStoredTenantId(newTenantId);
      // C1: the vertical is known right here, at creation, and the shell's
      // skin reads it later (lib/skin.ts). Stored beside the id so it survives
      // the reload. Since 2026-08-10 the SERVER'S stamp is what gets stored —
      // the row's truth, never this client's request.
      setStoredTenantVertical(stampedVertical ?? requestedVertical);
      setTenantId(newTenantId);
      if (isActorFlow) {
        // Actor-only: the singleton actor profile. Any other pack has no
        // such write — attempting it is exactly the 422 wall the 2026-08-10
        // walk hit.
        const fields: Record<string, unknown> = { professional_name: trimmed };
        if (withUnion && union) fields.union_name = UNION_NAME[union];
        await updateActorProfile(newTenantId, fields);
      }
      if (deploymentVertical === 'actor_assist') {
        // B2 §1 — do NOT continue into the address step with no performer.
        // A failure here leaves `claimError` set and the 'creating' step
        // renders it with a retry; createWorkspace itself is not re-entered
        // (see `retryClaim`), so the workspace this just created is never
        // duplicated.
        const claimed = await attemptClaim(newTenantId, trimmed, legalName.trim());
        if (!claimed) {
          setBusy(false);
          return;
        }
      }
      await finishProfileAndAdvance(newTenantId, trimmed, performerIsAdult);
    } catch (err) {
      setBusy(false);
      // The server's own sentence when it gave one; the network case named
      // for what it is — a person mid-setup needs to know whether anything
      // now exists under their name.
      setError(
        err instanceof ApiError ? err.message : 'Could not reach the server — nothing was saved.'
      );
      setStep(isActorFlow ? 'union' : 'name');
    }
  }

  /** The name step's own "Continue" — one function so the button and the
   *  Enter-key handler can never drift apart on where they send someone.
   *  B1: actor_assist gets the age step right after this one; every other
   *  pack is byte-for-byte unchanged. */
  function proceedFromName(): void {
    setError(null);
    if (isActorFlow) {
      setStep('union');
      return;
    }
    if (deploymentVertical === 'actor_assist') {
      setStep('age');
      return;
    }
    void createWorkspace(false);
  }

  /** B1 — the setup-age step's own answer. Required (no skip: one tap, and
   *  a skip would silently mean "minor" — the brief's own reasoning), so
   *  there is no "Skip for now" affordance here, unlike every other step in
   *  this screen. A failed WRITE must not trap them anyway — the tap
   *  itself is what is required, not a confirmed round trip — so setup
   *  proceeds either way, same posture `createWorkspace`'s own name-write
   *  failure already takes a few lines up. */
  async function answerAge(isAdult: boolean): Promise<void> {
    if (busy) return;
    setBusy(true);
    setError(null);
    // ⚑ B8 — held so `continueToPage` can decide, LOCALLY, whether this
    // account is even eligible for the page step, with no second round trip:
    // the tap itself already answered the one question that gate needs.
    setSelfIsAdult(isAdult);
    try {
      await recordAgeAttestation(isAdult);
    } catch {
      // Atlas's review, 2026-09-24: this used to swallow the failure and carry
      // on — but there is nowhere else to answer this question, so a lost
      // answer would silently leave the account read as a minor. Say so and
      // stay here; both buttons stay live, so a retry is one tap and nothing
      // is trapped.
      setBusy(false);
      setError(AGE_ANSWER_SAVE_FAILED_MESSAGE);
      return;
    }
    setBusy(false);
    void createWorkspace(false, isAdult);
  }

  /** The tail of setup, shared by the address step and the path that skips
   *  it, so the two can never drift apart. */
  function continueAfterAddress(newTenantId: string): void {
    if (browserSupportsWebAuthn()) {
      setBusy(false);
      setStep('passkey');
    } else {
      goToMaterialsOrFinish(newTenantId);
    }
  }

  /** Settle the handle, then move on.
   *
   * ⚠ A REFUSAL MUST NOT TRAP THEM — and it must not be SWALLOWED either.
   * Every refusal is said here and the step stays put; the "Keep <derived>"
   * link below is always live, so staying put is never a wall.
   *
   * ⚑ GAP 57 readiness (2026-09-29): this used to CONTINUE on any code other
   * than slug_taken/invalid_slug. The server answered `address_settled` (its
   * window had closed on the performer this screen creates one step
   * earlier), so a family that typed a new name kept the derived one and
   * nothing said so. The server's window now stays open through setup; this
   * side now shows whatever it does not recognise, in the server's own words. */
  async function settleAddress(): Promise<void> {
    if (!tenantId || busy) return;
    const wanted = handle.trim().toLowerCase();
    const current = (mailAddress ?? '').split('@')[0] ?? '';
    if (!wanted || wanted === current) {
      continueAfterAddress(tenantId);
      return;
    }
    setBusy(true);
    setHandleError(null);
    try {
      // The SERVER's composed address, not ours — it holds the format rule
      // (`inboundAddressFor`) and it is the one that decides what the handle
      // actually became. Keeping it means every later read on this screen
      // names the address that exists.
      const settled = await setMailboxSlug(tenantId, wanted);
      if (settled) setMailAddress(settled);
      continueAfterAddress(tenantId);
    } catch (err) {
      setBusy(false);
      if (err instanceof ApiError && (err.code === 'slug_taken' || err.code === 'invalid_slug')) {
        setHandleError(err.message);
        return;
      }
      setHandleError(
        err instanceof ApiError ? (err.serverMessage ?? err.message) : 'That name could not be saved.'
      );
    }
  }

  async function registerPasskey() {
    if (!tenantId || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { ceremonyId, options } = await passkeyRegisterOptions();
      const response = await startRegistration({
        optionsJSON: options as Parameters<typeof startRegistration>[0]['optionsJSON'],
      });
      await passkeyRegisterVerify(ceremonyId, response);
      goToMaterialsOrFinish(tenantId);
    } catch {
      // A declined/failed passkey must never block finishing — just proceed.
      goToMaterialsOrFinish(tenantId);
    }
  }

  if (step === 'creating') {
    // B2 §1 — a failed create/bind (the performer record, actor_assist
    // only) shows the server's own sentence right here rather than reverting
    // to an earlier step, and stays put with the button live for a retry:
    // the workspace already exists by this point, so going back would risk
    // a second `createTenant` on the next Continue.
    if (claimError) {
      return (
        <div class="onb">
          <div class="onb__center">
            <Knot size={46} />
            <div class="onb__say onb__say--tight" role="alert">
              {claimError}
            </div>
          </div>
          <button type="button" class="onb__btn" disabled={busy} onClick={() => void retryClaim()}>
            {busy ? 'Setting up…' : 'Try again'}
          </button>
        </div>
      );
    }
    return (
      <div class="onb">
        <div class="onb__center">
          <Knot size={46} />
          <div class="onb__say onb__say--tight">Setting up your workspace…</div>
        </div>
      </div>
    );
  }

  // The beat while the resumed walk asks what is still owed. Says where they
  // are, not what we are doing — they left in the middle of setting up, and
  // this is the line that tells them nothing was lost.
  if (step === 'resuming') {
    return (
      <div class="onb">
        <div class="onb__center">
          <Knot size={46} />
          <div class="onb__say onb__say--tight">Picking up where you left off…</div>
        </div>
      </div>
    );
  }

  if (step === 'passkey') {
    return (
      <div class="onb">
        {deploymentVertical === 'actor_assist' ? actorAssistDots('passkey') : null}
        <div class="onb__center">
          <Knot size={46} />
          <div class="onb__eyebrow">One more thing</div>
          <div class="onb__say">
            Faster sign-in?
            <br />
            <span class="onb__say-soft">
              Use Face or Touch ID next time instead of an emailed link.
            </span>
          </div>
        </div>
        <button type="button" class="onb__btn" disabled={busy} onClick={() => void registerPasskey()}>
          {busy ? 'Setting up…' : 'Set up Face / Touch ID'}
        </button>
        {/* C6a INVARIANT — everything is skippable except identity. This
            skip carries NO disabled= at all (it must never share a disabled
            predicate with the submit above): a WebAuthn ceremony that hangs
            forever leaves `busy` true, and on 2026-08-04 a stuck busy flag
            froze both controls and trapped a real person on this screen.
            Skipping is pure navigation (onResolved), safe at any moment —
            including mid-ceremony. */}
        <button type="button" class="onb__textlink" onClick={() => goToMaterialsOrFinish(tenantId!)}>
          Skip for now
        </button>
      </div>
    );
  }

  // B5 §1 — HEADSHOTS. Reuses MaterialsPhotos byte-for-byte (the LIVE
  // actor_assist photo surface — reads/writes `aa_media`, added `c49eb651d`)
  // rather than a second uploader: the same component Today's own Materials
  // tab renders, mounted here too (`kind="headshot"`, below — since
  // `bcc1db739` this stop saves `kind: 'headshot'`, not `'photo'`), so the
  // upload function is the one function in both places. `tenantId!` is
  // safe — this step is reachable only once the workspace and its performer
  // exist.
  if (step === 'headshots') {
    return (
      <div class="onb">
        {actorAssistDots('headshots')}
        {/* B7 (2026-09-24, `defa9aed`, item 5) — "One more thing" on its 3rd
            appearance here and its 4th on the résumé-import stop next was the
            walk's own filler running out. "Your materials" names what these
            two stops actually are. */}
        <div class="onb__eyebrow">Your materials</div>
        <div class="onb__say">
          Your headshots.
          <br />
          <span class="onb__say-soft">The photos casting sees first. Add one or several.</span>
        </div>
        <MaterialsPhotos tenantId={tenantId!} kind="headshot" />
        <div class="onb__grow" />
        {/* Both controls do the same thing — move on — because nothing here
            is a commitment to record beyond what MaterialsPhotos' own
            "+ Add photo" already saved as each file was picked. "Continue"
            and "Skip for now" read differently but ARE the same call, same
            as every other optional stop on this screen. */}
        <button type="button" class="onb__btn" onClick={() => afterHeadshots(tenantId!)}>
          Continue
        </button>
        <button type="button" class="onb__textlink" onClick={() => afterHeadshots(tenantId!)}>
          Skip for now
        </button>
      </div>
    );
  }

  // B5 §1 — BRING AN OLD RÉSUMÉ. The same door PerformerResume's "Bring an
  // old resume" opens (components/CaptureSheet's `requestDocumentCapture`
  // shape) — mounted directly here rather than via that module-level
  // pending-request trick, because App's own CaptureSheet instance is not in
  // this tree yet (Onboarding renders before the tenant shell does). Passing
  // `documentKind: 'old_resume'` straight in `context` opens the identical
  // server lane (`captureKnot` -> `document_kind:old_resume`,
  // actorAssistResumeImport.test.ts) through the identical component — no
  // second capture surface, no second upload path. Plan-gated: this step
  // only renders when `actorAssistSteps` already found `resume.import_old`
  // on the household's tier — every real tier, Free included, since B7
  // (2026-09-24, `defa9aed`) — so it is skipped, never shown locked, only
  // while the plan read is in flight or has failed.
  if (step === 'resume_import') {
    return (
      <div class="onb">
        {actorAssistDots('resume_import')}
        <div class="onb__eyebrow">Your materials</div>
        <div class="onb__say">
          Bring an old résumé.
          <br />
          <span class="onb__say-soft">Its credits become your history here — you approve each one.</span>
        </div>
        <div class="onb__grow" />
        <button type="button" class="onb__btn" onClick={() => setResumeSheetOpen(true)}>
          Bring an old résumé
        </button>
        {/* C6a INVARIANT — this control carries no disabled= and sits OUTSIDE
            the capture sheet entirely, so it stays live even while that
            sheet is mid-upload (its own `submitting`/`uploading` phase never
            touches this button's predicate). Skipping never opens the sheet
            at all. */}
        <button type="button" class="onb__textlink" onClick={() => continueToPage(tenantId!)}>
          Skip for now
        </button>
        <CaptureSheet
          open={resumeSheetOpen}
          name={name.trim() || 'there'}
          tenantId={tenantId!}
          context={{ documentKind: 'old_resume' }}
          onKnotState={() => {}}
          onClose={() => {
            // Fires on cancel, on decline, and — after the confirmed beat —
            // on approve (CaptureSheet's own CONFIRM_HOLD_MS timer). All
            // three are a settled decision about this stop; none of them
            // should reopen it.
            setResumeSheetOpen(false);
            continueToPage(tenantId!);
          }}
        />
      </div>
    );
  }

  // B8 — the page step itself. `tenantId!` is safe for the same reason the
  // headshots/resume_import steps already rely on: reachable only once the
  // workspace and its performer exist.
  if (step === 'page') {
    return <YourPage tenantId={tenantId!} mode="onboarding" onDone={() => finishSetup(tenantId!)} />;
  }

  if (step === 'address') {
    const domain = (mailAddress ?? '').split('@')[1] ?? '';
    return (
      <div class="onb">
        {deploymentVertical === 'actor_assist' ? (
          actorAssistDots('address')
        ) : (
          <div class="onb__dots">
            <i class="onb__dot onb__dot--on" />
            <i class="onb__dot onb__dot--on" />
          </div>
        )}
        <div class="onb__eyebrow">Your own address</div>
        <div class="onb__say">
          Forward an audition notice here.
          <br />
          {/* ⚠ SAYS THE WINDOW CLOSES, HERE, WHERE IT IS STILL OPEN. Huan,
              2026-08-28: settled at setup, not changeable in Settings after.
              A person who is not told that will discover it later, having
              already given the address to someone. */}
          <span class="onb__say-soft">
            It's read for you, and you approve what it finds. Choose the name
            now — it stays with the account.
          </span>
        </div>

        <label class="onb__label" for="onb-handle">
          The name in your address
        </label>
        {/* ⚠ NO PREFIX IN FRONT OF THE INPUT ANY MORE (Huan, 2026-09-11:
            "take it out please. ugly"). What a family types IS the whole
            local-part — the address is their name and the domain, nothing
            else — so the only fixed text here is the `@domain` tail. */}
        <div class="onb__addr">
          <input
            id="onb-handle"
            class="onb__input onb__input--addr"
            value={handle}
            autocomplete="off"
            spellcheck={false}
            /* ⚑ LOWER CASE AS THEY TYPE (2026-09-15, the Day 1 walk).
               An address handle IS lower case — `isValidTenantSlug` and the
               ingest parser both say so, and the route lowercased the value
               before validating it. So "MyName" was accepted, silently became
               "myname", and the family learned it afterwards from an address
               they had already been handed. Normalising HERE means what is on
               the screen and what is stored are the same string at every
               moment: nothing is changed behind them, and no caption has to
               explain a rule the field is now obeying in front of them. */
            onInput={(e) => {
              const el = e.target as HTMLInputElement;
              const lowered = el.value.toLowerCase();
              // Write it back to the DOM too, or the visible field keeps the
              // capital while state holds the lowered copy — which is the
              // silent divergence this is here to end.
              if (el.value !== lowered) el.value = lowered;
              setHandle(lowered);
              setHandleError(null);
            }}
          />
          <span class="onb__addr-fix">@{domain}</span>
        </div>
        {handleError ? <div class="onb__error">{handleError}</div> : null}

        <button type="button" class="onb__btn" disabled={busy} onClick={() => void settleAddress()}>
          {busy ? 'Saving…' : 'That’s the one'}
        </button>
        {/* Same C6a invariant as the passkey step: the move-on control carries
            no disabled predicate, so a stuck flag can never trap anyone here
            over a cosmetic choice. Keeping the derived name is a real answer. */}
        <button
          type="button"
          class="onb__textlink"
          onClick={() => tenantId && continueAfterAddress(tenantId)}
        >
          Keep {mailAddress ? mailAddress.split('@')[0] : 'the suggested name'}
        </button>
      </div>
    );
  }

  if (step === 'age') {
    const performerName = name.trim();
    return (
      <div class="onb">
        {actorAssistDots('age')}
        <div class="onb__eyebrow">One more thing</div>
        {/* B12 — extracted into AgeQuestion.tsx so this and YourPage.tsx's
            own in-place ask can never drift apart again. */}
        <AgeQuestion
          performerName={performerName}
          busy={busy}
          error={error}
          onAnswer={(isAdult) => void answerAge(isAdult)}
        />
      </div>
    );
  }

  if (step === 'union') {
    return (
      <div class="onb">
        <div class="onb__dots">
          <i class="onb__dot onb__dot--on" />
          <i class="onb__dot onb__dot--on" />
        </div>
        <div class="onb__eyebrow">A little about you</div>
        <div class="onb__say">
          Your union.
          <br />
          <span class="onb__say-soft">You can change this later.</span>
        </div>
        <div class="onb__chips" role="group" aria-label="Union">
          {UNIONS.map((u) => (
            <button
              type="button"
              key={u}
              class={`onb__chip${union === u ? ' onb__chip--on' : ''}`}
              aria-pressed={union === u}
              onClick={() => setUnion(u)}
            >
              {u}
            </button>
          ))}
        </div>
        {error ? (
          <div class="onb__error" role="alert">
            {error}
          </div>
        ) : null}
        <div class="onb__grow" />
        <button type="button" class="onb__btn" disabled={busy} onClick={() => void createWorkspace(true)}>
          Continue
        </button>
        {/* C6a INVARIANT: no disabled= (see the passkey step's skip) —
            createWorkspace's own busy guard is what prevents a double
            submit, not a shared disabled predicate. */}
        <button type="button" class="onb__textlink" onClick={() => void createWorkspace(false)}>
          Skip for now
        </button>
        {/* ⚑ A WAY BACK (2026-09-15, the Day 1 walk: "onboarding advanced with
            no back button"). This step is the only one where going back is
            pure navigation — nothing exists yet. From the ADDRESS step it is
            not: the workspace has already been created by then, so "back"
            would be an edit rather than a return, and the one field that
            settles there is editable in place until they press. */}
        <button type="button" class="onb__textlink" onClick={() => { setError(null); setStep('name'); }}>
          Back
        </button>
      </div>
    );
  }

  // step === 'name'
  // The dots count the flow that actually runs: the assist pack has no union
  // step, so promising a second dot there is a step that never comes.
  // B1: actor_assist now runs a second step too (the setup-age question),
  // so it counted as 2 dots the same way the actor flow's union step does.
  // B2 §3: actor_assist's own count is now the real, dynamic one
  // (`actorAssistDots`) — name/age always, address/passkey only when they
  // will actually be asked. Every other pack keeps this fixed count.
  const dotTotal = isActorFlow ? 2 : 1;
  const nameStepReady =
    deploymentVertical === 'actor_assist' ? Boolean(name.trim() && legalName.trim()) : Boolean(name.trim());
  return (
    <div class="onb">
      {deploymentVertical === 'actor_assist' ? (
        actorAssistDots('name')
      ) : (
        <div class="onb__dots">
          {Array.from({ length: dotTotal }, (_, i) => (
            <i key={i} class={`onb__dot${i === 0 ? ' onb__dot--on' : ''}`} />
          ))}
        </div>
      )}
      {notice ? <div class="onb__notice">{notice}</div> : null}
      <div class="onb__eyebrow">Welcome — let’s set up</div>
      <div class="onb__say">
        First, your name.
        <br />
        {/* The assist flow never reaches a resume from here — the sub-line
            must not promise one.
            ⚑ 2026-09-19 — "you'll add performers next" IS DELETED FOR
            actor_assist. An account is one career (Huan's ruling); this
            generic branch's own promise is written for the OTHER packs that
            still take it (found on a real walk — actor_assist reaches this
            branch too, since `isActorFlow` above tests 'actors' alone and
            fixing THAT is a deeper gap this chunk reports rather than
            patches; see this file's header). Whoever lands here still owes
            the truth. */}
        <span class="onb__say-soft">
          {isActorFlow || deploymentVertical === 'actor_assist'
            ? 'The one on your resume.'
            : 'How the app greets you — you’ll add performers next.'}
        </span>
      </div>
      <label class="onb__label" for="onb-name">
        Professional name
      </label>
      <input
        id="onb-name"
        class="onb__input"
        type="text"
        autocomplete="name"
        placeholder="e.g. Alex Rivera"
        value={name}
        onInput={(e) => setName((e.target as HTMLInputElement).value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && nameStepReady) proceedFromName();
        }}
      />
      {/* B2 §1 — actor_assist only: "Legal name upfront. Cleaner that way as
          they are in setup mode." (Huan). Both fields are required; Today's
          own ClaimYourself asks the same two and stays the fallback for
          every account this change does not reach. */}
      {deploymentVertical === 'actor_assist' ? (
        <>
          <label class="onb__label" for="onb-legal-name">
            Legal name
          </label>
          <input
            id="onb-legal-name"
            class="onb__input"
            type="text"
            autocomplete="name"
            placeholder="As it appears on a contract"
            value={legalName}
            onInput={(e) => setLegalName((e.target as HTMLInputElement).value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && nameStepReady) proceedFromName();
            }}
          />
        </>
      ) : null}
      {error ? (
        <div class="onb__error" role="alert">
          {error}
        </div>
      ) : null}
      <div class="onb__grow" />
      <button type="button" class="onb__btn" disabled={!nameStepReady} onClick={proceedFromName}>
        Continue
      </button>
      {/* The coach escape. This screen renders for every signed-in account
          with zero workspaces — which is exactly what a coach is and must
          stay (CoachShell's own header: a coach who gains a membership stops
          being outside the family). One quiet line out, so a coach who
          signed in here is not funnelled into creating a household. */}
      <a class="onb__textlink" href="#/pro">
        Here to review tapes? Your coach desk is this way.
      </a>
    </div>
  );
}
